/**
 * Workflow tool management utilities
 * Pure functions for managing workflow agents and their tools
 */

/**
 * Represents a trigger configuration for an agent
 */
export interface Trigger {
  /** Source of the trigger (e.g., "input") */
  source: string;
  /** Mapping of source fields to agent parameters */
  mapping: Record<string, any>;
}

/**
 * Represents an agent within a workflow
 */
export interface Agent {
  /** Type of agent (e.g., "chat_agent") */
  type: string;
  /** Agent parameters including tools configuration */
  parameters: {
    /** Tools available to the agent */
    tools?: string[];
    [key: string]: any;
  };
  /** Trigger configurations for the agent */
  triggers: Trigger[];
  [key: string]: any;
}

/**
 * Represents a complete workflow configuration
 */
export interface Workflow {
  /** Unique workflow identifier */
  id: string;
  /** Human-readable workflow name */
  name: string;
  /** Tenant identifier */
  tenant: string;
  /** Workflow description */
  description: string;
  /** Input configuration for the workflow */
  input: Record<string, any> | null | undefined;
  /** Map of agent IDs to agent configurations */
  agents: Record<string, Agent>;
  [key: string]: any;
}

/**
 * Finds the chat agent in a workflow
 * Chat agents are identified by type === "chat_agent"
 *
 * @param workflow - The workflow to search
 * @returns Tuple of [agentId, agent] if found, null otherwise
 */
export const findChatAgent = (workflow: Workflow | null | undefined): [string, Agent] | null => {
  if (!workflow?.agents) {
    return null;
  }

  const chatAgentEntry = Object.entries(workflow.agents).find(
    ([_, agent]) => agent.type === "chat_agent",
  );

  return chatAgentEntry || null;
};

/**
 * Gets the current tools for an agent
 *
 * @param agent - The agent to get tools from
 * @returns Array of tool names
 */
export const getAgentTools = (agent: Agent): string[] => {
  return agent.parameters?.tools || [];
};

/**
 * Updates an agent's tools list
 *
 * @param agent - The agent to update
 * @param tools - Tools to add, remove, or set
 * @param action - Action to perform: "add", "remove", or "set"
 * @returns Updated agent with modified tools
 */
export const updateAgentTools = (
  agent: Agent,
  tools: string[],
  action: "add" | "remove" | "set",
): Agent => {
  const currentTools = getAgentTools(agent);

  let updatedTools: string[];

  switch (action) {
    case "add":
      // Add tools that aren't already present
      updatedTools = [...currentTools, ...tools.filter((tool) => !currentTools.includes(tool))];
      break;

    case "remove":
      // Remove specified tools
      updatedTools = currentTools.filter((tool) => !tools.includes(tool));
      break;

    case "set":
      // Replace all tools
      updatedTools = tools;
      break;

    default:
      updatedTools = currentTools;
  }

  // Update tools in parameters only
  return {
    ...agent,
    parameters: {
      ...(agent.parameters || {}),
      tools: updatedTools,
    },
  };
};

/**
 * Toggles a single tool in a workflow's chat agent
 * Adds the tool if not present, removes if present
 *
 * @param workflow - The workflow to modify
 * @param tool - Tool name to toggle
 * @returns Updated workflow or null if no chat agent found
 */
export const toggleWorkflowTool = (
  workflow: Workflow | null | undefined,
  tool: string,
): Workflow | null => {
  const chatAgentEntry = findChatAgent(workflow);

  if (!chatAgentEntry || !workflow) {
    return workflow || null;
  }

  const [agentId, agent] = chatAgentEntry;
  const currentTools = getAgentTools(agent);
  const hasTool = currentTools.includes(tool);

  // Toggle: remove if present, add if not
  const action = hasTool ? "remove" : "add";
  const updatedAgent = updateAgentTools(agent, [tool], action);

  return {
    ...workflow,
    agents: {
      ...workflow.agents,
      [agentId]: updatedAgent,
    },
  };
};

/**
 * Sets workflow tools to enabled or disabled state
 * Batch operation to add or remove multiple tools at once
 *
 * @param workflow - The workflow to modify
 * @param tools - Single tool or array of tools to modify
 * @param enabled - True to add tools, false to remove them
 * @returns Updated workflow or null if no chat agent found
 */
export const setWorkflowTools = (
  workflow: Workflow | null | undefined,
  tools: string | string[],
  enabled: boolean,
): Workflow | null => {
  const chatAgentEntry = findChatAgent(workflow);

  if (!chatAgentEntry || !workflow) {
    return workflow || null;
  }

  const [agentId, agent] = chatAgentEntry;
  const toolsArray = Array.isArray(tools) ? tools : [tools];
  const action = enabled ? "add" : "remove";
  const updatedAgent = updateAgentTools(agent, toolsArray, action);

  return {
    ...workflow,
    agents: {
      ...workflow.agents,
      [agentId]: updatedAgent,
    },
  };
};

/**
 * Checks if a workflow has a specific tool enabled
 *
 * @param workflow - The workflow to check
 * @param tool - Tool name to check for
 * @returns True if the tool is enabled, false otherwise
 */
export const hasWorkflowTool = (workflow: Workflow | null | undefined, tool: string): boolean => {
  const chatAgentEntry = findChatAgent(workflow);

  if (!chatAgentEntry) {
    return false;
  }

  const [, agent] = chatAgentEntry;
  const tools = getAgentTools(agent);

  return tools.includes(tool);
};

/**
 * Checks if any of the specified tools are enabled in the workflow
 *
 * @param workflow - The workflow to check
 * @param tools - Array of tool names to check for
 * @returns True if any of the tools are enabled, false otherwise
 */
export const hasAnyWorkflowTool = (
  workflow: Workflow | null | undefined,
  tools: string[],
): boolean => {
  const chatAgentEntry = findChatAgent(workflow);

  if (!chatAgentEntry) {
    return false;
  }

  const [, agent] = chatAgentEntry;
  const agentTools = getAgentTools(agent);

  return tools.some((tool) => agentTools.includes(tool));
};

/**
 * Creates a default chat workflow structure
 * Used when no workflow exists and file upload is initiated
 *
 * @param model - AI model to use (default: "gpt-4.1")
 * @returns New workflow with basic chat agent configuration
 */
export const createDefaultChatWorkflow = (model: string = "gpt-4.1"): Workflow => {
  return {
    name: "Chat Agent",
    id: "chat_agent",
    tenant: "default",
    description: "Chat with AI assistant",
    input: null,
    agents: {
      chat_agent: {
        type: "chat_agent",
        parameters: {
          model,
          tools: ["save_to_memory"],
        },
        triggers: [{ source: "input", mapping: { prompt: "prompt" } }],
      },
    },
  };
};

// ---------------------------------------------------------------------------
// Skill utilities — mirror the tool utilities above for skill management
// ---------------------------------------------------------------------------

/**
 * Gets the current skills for an agent
 *
 * @param agent - The agent to get skills from
 * @returns Array of skill IDs
 */
export const getAgentSkills = (agent: Agent): string[] => {
  // `parameters` is loosely typed, so guard against a non-array `skills` value
  // (e.g. stray legacy data) that would make hasWorkflowSkill do substring
  // matches on a string rather than exact id matches on a list.
  const skills = agent.parameters?.skills;

  return Array.isArray(skills) ? skills : [];
};

/**
 * Checks if a workflow has a specific skill enabled
 *
 * @param workflow - The workflow to check
 * @param skillId - Skill ID to check for
 * @returns True if the skill is enabled, false otherwise
 */
export const hasWorkflowSkill = (
  workflow: Workflow | null | undefined,
  skillId: string,
): boolean => {
  const chatAgentEntry = findChatAgent(workflow);

  if (!chatAgentEntry) {
    return false;
  }

  const [, agent] = chatAgentEntry;

  return getAgentSkills(agent).includes(skillId);
};

/**
 * Toggles a single skill in a workflow's chat agent
 * Adds the skill if not present, removes if present
 *
 * @param workflow - The workflow to modify
 * @param skillId - Skill ID to toggle
 * @returns Updated workflow or null if no chat agent found
 */
export const toggleWorkflowSkill = (
  workflow: Workflow | null | undefined,
  skillId: string,
): Workflow | null => {
  const chatAgentEntry = findChatAgent(workflow);

  if (!chatAgentEntry || !workflow) {
    return workflow || null;
  }

  const [agentId, agent] = chatAgentEntry;
  const currentSkills = getAgentSkills(agent);
  const hasSkill = currentSkills.includes(skillId);

  const updatedSkills = hasSkill
    ? currentSkills.filter((s) => s !== skillId)
    : [...currentSkills, skillId];

  return {
    ...workflow,
    agents: {
      ...workflow.agents,
      [agentId]: {
        ...agent,
        parameters: {
          ...(agent.parameters || {}),
          skills: updatedSkills,
        },
      },
    },
  };
};

/**
 * Merges tools into a workflow while preserving other agent properties
 * Useful for applying saved preferences or bulk updates
 *
 * @param workflow - The workflow to update
 * @param tools - Tools to merge into the workflow
 * @param merge - "replace" to replace all tools, "add" to append
 * @returns Updated workflow with merged tools
 */
export const mergeWorkflowTools = (
  workflow: Workflow,
  tools: string[],
  merge: "replace" | "add" = "replace",
): Workflow => {
  const chatAgentEntry = findChatAgent(workflow);

  if (!chatAgentEntry) {
    return workflow;
  }

  const [agentId, agent] = chatAgentEntry;
  const action = merge === "replace" ? "set" : "add";
  const updatedAgent = updateAgentTools(agent, tools, action);

  return {
    ...workflow,
    agents: {
      ...workflow.agents,
      [agentId]: updatedAgent,
    },
  };
};
